import React from 'react'
import { Filter } from 'storfox-filter'
import Grid from '@mui/material/Grid'
import { DateTimeField } from 'storfox-form-fields'
import { prop } from 'ramda'
import Typography from '@mui/material/Typography'
import { useTranslation } from 'react-i18next'

import { escapeAtob, unescapeBtoa } from '~/utils'
import WarehouseMultiSelectField from '~/components/Fields/WarehouseMultiSelectField'
import Accordion from '~/components/Accordion'
import { getDateTimeGMTDifference, getDateTimeGMTUser } from '~/utils/date'

import ShipmentStatusField from './ShipmentStatusField'
import ShipmentDirectionField from './ShipmentDirectionField'
import ShipmentCompanyList from './ShipmentCompanyList'

import { FILTER_NAME as name } from '../../constants/Shipment'

export const toParams = values => {
  const direction = prop('direction', values)
  const companyObject = prop('company', values)
  const status = prop('status', values)

  const warehouses = unescapeBtoa(prop('warehouses', values))
  const company = unescapeBtoa(companyObject)

  const shippedDateStart = getDateTimeGMTDifference(prop('shippedDateStart', values))
  const shippedDateEnd = getDateTimeGMTDifference(prop('shippedDateEnd', values))
  const deliveredDateStart = getDateTimeGMTDifference(prop('deliveredDateStart', values))
  const deliveredDateEnd = getDateTimeGMTDifference(prop('deliveredDateEnd', values))
  const createdAtStart = getDateTimeGMTDifference(prop('createdAtStart', values))
  const createdAtEnd = getDateTimeGMTDifference(prop('createdAtEnd', values))
  const updatedAtStart = getDateTimeGMTDifference(prop('updatedAtStart', values))
  const updatedAtEnd = getDateTimeGMTDifference(prop('updatedAtEnd', values))
  const orderedAtStart = getDateTimeGMTDifference(prop('orderedAtStart', values))
  const orderedAtEnd = getDateTimeGMTDifference(prop('orderedAtEnd', values))

  return {
    direction,
    status,
    warehouses,
    orderedAtStart,
    orderedAtEnd,
    shippedDateStart,
    shippedDateEnd,
    deliveredDateStart,
    deliveredDateEnd,
    createdAtStart,
    createdAtEnd,
    updatedAtStart,
    updatedAtEnd,
    company
  }
}

export const toValues = params => {
  const direction = prop('direction', params)
  const status = prop('status', params)
  const warehouses = escapeAtob(prop('warehouses', params))
  const company = escapeAtob(prop('company', params))

  const shippedDateStart = getDateTimeGMTUser(prop('shippedDateStart', params))
  const shippedDateEnd = getDateTimeGMTUser(prop('shippedDateEnd', params))
  const deliveredDateStart = getDateTimeGMTUser(prop('deliveredDateStart', params))
  const deliveredDateEnd = getDateTimeGMTUser(prop('deliveredDateEnd', params))
  const createdAtStart = getDateTimeGMTUser(prop('createdAtStart', params))
  const createdAtEnd = getDateTimeGMTUser(prop('createdAtEnd', params))
  const updatedAtStart = getDateTimeGMTUser(prop('updatedAtStart', params))
  const updatedAtEnd = getDateTimeGMTUser(prop('updatedAtEnd', params))
  const orderedAtStart = getDateTimeGMTUser(prop('orderedAtStart', params))
  const orderedAtEnd = getDateTimeGMTUser(prop('orderedAtEnd', params))

  return {
    direction,
    status,
    warehouses,
    orderedAtStart,
    orderedAtEnd,
    shippedDateStart,
    shippedDateEnd,
    deliveredDateStart,
    deliveredDateEnd,
    createdAtStart,
    createdAtEnd,
    updatedAtStart,
    updatedAtEnd,
    company
  }
}
export const transformer = { toParams, toValues }

export const shipmentFilterOptions = {
  name,
  transformer
}

function ShipmentFilterForm (props) {
  const { t } = useTranslation()
  return (
    <Filter {...props}>
      <Accordion title="General" defaultExpanded={true}>
        <Grid container={true} spacing={2}>
          <Grid item={true} xs={12}>
            <ShipmentDirectionField />
          </Grid>
          <Grid item={true} xs={12}>
            <ShipmentStatusField />
          </Grid>
          <Grid item={true} xs={12}>
            <WarehouseMultiSelectField />
          </Grid>
          <Grid item={true} xs={12}>
            <ShipmentCompanyList />
          </Grid>
        </Grid>
      </Accordion>
      <Accordion title="Date">
        <Grid container={true} spacing={2}>
          <Grid item={true} xs={12}>
            <Typography variant="body2" gutterBottom={true}>
              {t('Ordered')}:
            </Typography>
            <Grid container={true} spacing={2}>
              <Grid item={true} xs={12}>
                <DateTimeField name="orderedAtStart" label="Start" />
              </Grid>
              <Grid item={true} xs={12}>
                <DateTimeField name="orderedAtEnd" label="End" />
              </Grid>
            </Grid>
          </Grid>

          <Grid item={true} xs={12}>
            <Typography variant="body2" gutterBottom={true}>
              {t('Shipped')}:
            </Typography>
            <Grid container={true} spacing={2}>
              <Grid item={true} xs={12}>
                <DateTimeField name="shippedDateStart" label="Start" />
              </Grid>
              <Grid item={true} xs={12}>
                <DateTimeField name="shippedDateEnd" label="End" />
              </Grid>
            </Grid>
          </Grid>

          <Grid item={true} xs={12}>
            <Typography variant="body2" gutterBottom={true}>
              {t('Delivered')}:
            </Typography>
            <Grid container={true} spacing={2}>
              <Grid item={true} xs={12}>
                <DateTimeField name="deliveredDateStart" label="Start" />
              </Grid>
              <Grid item={true} xs={12}>
                <DateTimeField name="deliveredDateEnd" label="End" />
              </Grid>
            </Grid>
          </Grid>

          <Grid item={true} xs={12}>
            <Typography variant="body2" gutterBottom={true}>
              {t('Created')}:
            </Typography>
            <Grid container={true} spacing={2}>
              <Grid item={true} xs={12}>
                <DateTimeField name="createdAtStart" label="Start" />
              </Grid>
              <Grid item={true} xs={12}>
                <DateTimeField name="createdAtEnd" label="End" />
              </Grid>
            </Grid>
          </Grid>

          <Grid item={true} xs={12}>
            <Typography variant="body2" gutterBottom={true}>
              {t('Updated')}:
            </Typography>
            <Grid container={true} spacing={2}>
              <Grid item={true} xs={12}>
                <DateTimeField name="updatedAtStart" label="Start" />
              </Grid>
              <Grid item={true} xs={12}>
                <DateTimeField name="updatedAtEnd" label="End" />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Accordion>
    </Filter>
  )
}

export default ShipmentFilterForm
